/**
 * Labstep
 *
 * @module components/PermissionRole/Table
 * @desc Table for PermissionRole
 */

import { EntityActionUpdateDropdownPill } from 'labstep-web/components/Entity/Action/Update/DropdownPill';
import { CanCreate } from 'labstep-web/components/Entity/Can';
import {
  Action,
  PermissionRoleActionMenu,
} from 'labstep-web/components/PermissionRole/Action/Menu';
import { PermissionRoleFormCreate } from 'labstep-web/components/PermissionRole/Form/Create';
import {
  DEFAULT_PERMISSION_ROLE_SETTINGS,
  PERMISSION_LABELS,
} from 'labstep-web/constants/roles';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { ActionMenu } from 'labstep-web/core/Action/Menu';
import { DropdownPillTrigger } from 'labstep-web/core/Dropdown/Pill/Trigger';
import { Hoverable } from 'labstep-web/core/Hoverable';
import { Icon } from 'labstep-web/core/Icon';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { TableSimpleCollapsible } from 'labstep-web/core/Table/Simple/Collapsible';
import type { TableSimpleCollapsibleProps } from 'labstep-web/core/Table/Simple/Collapsible/types';
import { PermissionRole } from 'labstep-web/models/permission-role.model';
import { PermissionRoleSetting } from 'labstep-web/models/permission-role-setting.model';
import type React from 'react';
import styles from './styles.module.scss';
import type {
  IPermissionRoleTableProps,
  PermissionRoleTableContainerProps,
} from './types';
import {
  getExistingPermissionRoleSettingType,
  getItems,
  getSections,
} from './utils';

type PermissionRoleTableSectionType = {
  name: string;
  entityName: string;
  permission: string;
};

export const PermissionRoleTable: React.FC<
  IPermissionRoleTableProps
> = ({ create, permissionRoles, organization }) => {
  const columns: TableSimpleCollapsibleProps['columns'] = [
    {
      header: <div style={{ width: '210px' }} />,
      content: ({ name }: PermissionRoleTableSectionType) => (
        <span style={{ marginLeft: '50px' }}>
          {PERMISSION_LABELS[name]}
        </span>
      ),
      cellProps: { width: 5 },
    },
  ];

  DEFAULT_PERMISSION_ROLE_SETTINGS.forEach((permissionRole) => {
    columns.push({
      header: (
        <Hoverable
          component={
            <CanCreate
              parentName={organization.entityName}
              parentId={organization.id}
              entityName={PermissionRole.entityName}
            >
              <ActionMenu>
                <ModalDefault
                  header={`Duplicate ${permissionRole.name}`}
                  content={({ toggleModal }) => (
                    <PermissionRoleFormCreate
                      organization={organization}
                      options={{
                        onSuccess: toggleModal,
                      }}
                      body={{
                        permission_role_settings:
                          permissionRole.permission_role_settings,
                      }}
                      onCancel={toggleModal}
                    />
                  )}
                  viewComponent={({ toggleModal }) => (
                    <ActionComponent
                      type="option"
                      icon="clone"
                      text="Duplicate"
                      onClick={toggleModal}
                    />
                  )}
                />
              </ActionMenu>
            </CanCreate>
          }
          hoverable={
            <span>
              {permissionRole.name}
              <Icon
                name="lock"
                popup={{
                  content:
                    'This is a system role that cannot be edited or deleted',
                }}
              />
            </span>
          }
        />
      ),
      content: ({
        entityName,
        permission,
      }: PermissionRoleTableSectionType) => (
        <DropdownPillTrigger
          activeLabel={getExistingPermissionRoleSettingType(
            permissionRole,
            entityName,
            permission,
          )}
          disabled
        />
      ),
      cellProps: { style: { minWidth: 100 } },
    });
  });

  permissionRoles.forEach((permissionRole) => {
    columns.push({
      header: (
        <Hoverable
          component={
            <CanCreate
              parentName={organization.entityName}
              parentId={organization.id}
              entityName={PermissionRole.entityName}
            >
              <PermissionRoleActionMenu
                permissionRole={permissionRole}
                actions={[
                  Action.edit,
                  Action.duplicate,
                  Action.delete,
                ]}
              />
            </CanCreate>
          }
          hoverable={<span>{permissionRole.name}</span>}
        />
      ),
      content: ({
        entityName,
        permission,
      }: PermissionRoleTableSectionType) => (
        <EntityActionUpdateDropdownPill
          entity={permissionRole}
          currentValue={getExistingPermissionRoleSettingType(
            permissionRole,
            entityName,
            permission,
          )}
          items={getItems(
            permissionRole,
            entityName,
            permission,
            create,
          )}
        />
      ),
      cellProps: { style: { minWidth: 100 } },
    });
  });

  const sections = getSections();

  return (
    <div className={styles.container}>
      <TableSimpleCollapsible
        colSpan={1}
        columns={columns}
        sections={sections}
        fixed={false}
      />
    </div>
  );
};

export const PermissionRoleTableContainer: React.FC<
  PermissionRoleTableContainerProps
> = (props) => (
  <EntityCreateContainer
    entityName={PermissionRoleSetting.entityName}
    batch
  >
    {(containerProps) => (
      <PermissionRoleTable {...containerProps} {...props} />
    )}
  </EntityCreateContainer>
);
